import { baseAxios } from '../utils/axiosConfig';

// What the browser keeps after any successful sign-in: the login state for the
// UI only (the tokens themselves stay in httpOnly cookies). One home, so every
// way of signing in leaves the browser in the same state.
const rememberSignedIn = (response) => {
  localStorage.setItem('isLoggedIn', 'true');
  return response.data;
};

/**
 * Authentication service for handling login, logout, and token management
 * Improved security by not storing tokens in localStorage - relies on httpOnly cookies set by backend
 */
export const authService = {
  /**
   * Log in a user with the provided credentials
   * @param {Object} credentials - Object containing email and password
   * @returns {Promise} - Promise resolving to the login response
   */
  login: async (credentials) => {
    try {
      const response = await baseAxios.post('/api/token/', credentials);
      return rememberSignedIn(response);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },
  
  /**
   * Test system only: the made-up test people this server lets you sign in as.
   * Only a development build asks (the test system runs the development
   * server); live is a production build, answers an empty list here and never
   * sends the request at all. A server that is not a test system has no such
   * address and answers 404 (callers show nothing then).
   * @returns {Promise<Array>} - [{key, name, email, area, what_they_can_do}]
   */
  listTestPeople: async () => {
    // IMT's screens are a Create React App build: a live (production) build
    // has 'production' written in here, so this line returns before any
    // request exists. Read here, in the service, not in the panel, so the
    // design check's stand-in service decides it there.
    if (process.env.NODE_ENV !== 'development') return [];
    const response = await baseAxios.get('/api/authentication/test-people/');
    const people = response.status === 200 ? response.data && response.data.people : null;
    return Array.isArray(people) ? people : [];
  },

  /**
   * Test system only: sign in as one test person, no password typed. Answers
   * exactly like login() above.
   * @param {string} key - the test person's key, e.g. 'bunkers_ops'
   */
  signInAsTestPerson: async (key) => {
    const response = await baseAxios.post('/api/authentication/test-people/sign-in/', { key });
    return rememberSignedIn(response);
  },

  /**
   * Log out the current user
   * @returns {Promise<boolean>} - Whether logout was successful
   */
  logout: async () => {
    try {
      // No need to send the refresh token as it will be read from the cookie
      await baseAxios.post('/api/authentication/logout/');
      
      // Clear login state
      localStorage.removeItem('isLoggedIn');
      
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      return false;
    }
  },
  
  /**
   * Check if user is currently logged in
   * @returns {boolean} - Whether user is logged in
   */
  isLoggedIn: () => {
    return localStorage.getItem('isLoggedIn') === 'true';
  }
};

export default authService; 